import { useState } from 'react';
import { cn } from '@/lib/utils';
import { FOUNDER } from '@/lib/brand';

interface Props {
    /** Pixel size of the square avatar (width = height). Default 40. */
    size?: number;
    /** Show a green "online" dot on the bottom-right corner. */
    showOnlineDot?: boolean;
    /** Draw a 2px white/20 border for larger hero presentations. */
    ringBorder?: boolean;
    /** Extra class on the outer wrapper (positioning, margins). */
    className?: string;
    /** Override the rounded-corner shape. Default: rounded-full. */
    rounded?: string;
}

/**
 * Consistent founder avatar with graceful image-load fallback.
 * If the image 404s, we show a gradient tile with a bold "T" initial
 * (matches the TRBD brand mark used elsewhere).
 */
const FounderAvatar = ({
    size = 40,
    showOnlineDot = false,
    ringBorder = false,
    className,
    rounded = 'rounded-full',
}: Props) => {
    const [failed, setFailed] = useState(false);
    const dotSize = Math.max(10, Math.round(size * 0.22));

    return (
        <div
            className={cn('relative shrink-0', className)}
            style={{ width: size, height: size }}
        >
            <div
                className={cn(
                    'w-full h-full flex items-center justify-center overflow-hidden',
                    'bg-gradient-to-br from-primary to-secondary',
                    ringBorder ? 'border-2 border-white/20' : 'border border-white/20',
                    rounded
                )}
            >
                {!failed ? (
                    <img
                        src={FOUNDER.avatar}
                        alt={FOUNDER.nameAr}
                        className="w-full h-full object-cover"
                        onError={() => setFailed(true)}
                    />
                ) : (
                    <span
                        className="text-white font-bold pointer-events-none"
                        style={{ fontSize: Math.round(size * 0.4) }}
                    >
                        T
                    </span>
                )}
            </div>
            {showOnlineDot && (
                <span
                    className="absolute -bottom-0.5 -right-0.5 rounded-full bg-green-500 border-2 border-card"
                    style={{ width: dotSize, height: dotSize }}
                    aria-label="online"
                />
            )}
        </div>
    );
};

export default FounderAvatar;
